import React, { useMemo } from "react";
import { useRecoilValueLoadable } from "recoil";
import { IEvidence, IUser, ProgramData, ProgramSkill } from "@interfaces/Interfaces";
import { evidenceAtom, userAtom } from "@state/State";
import { addDays, addMonths, isBefore, startOfDay } from "date-fns";
import _ from "lodash";
import SegmentedProgressBar from "@components/common/SegmentedProgressBar";
import * as EvidenceUtils from "@utils/evidenceUtils";
import * as ProgramUtils from "@utils/programUtils";

const COMPLIANCE_MONTHS = 12;
const WARNING_DAYS = 30;

type Props = {
  program: ProgramData;
};

const evidenceIncludesSkill = (evidence: IEvidence, skill: ProgramSkill, program: ProgramData): boolean => {
  if (evidence.draft || evidence.evidenceJSON?.OnHoldReset === 1) {
    return false;
  }

  return ProgramUtils.evidenceMatchesSkill(evidence, skill, program);
};

const latestEvidenceDate = (skill: ProgramSkill, evidence: IEvidence[], program: ProgramData): Date | undefined => {
  const timestamps = evidence
    .filter((item) => evidenceIncludesSkill(item, skill, program))
    .map((item) => EvidenceUtils.getRenfrewEvidenceDate(item)?.getTime())
    .filter((time): time is number => time !== undefined && !isNaN(time));

  return timestamps.length > 0 ? startOfDay(new Date(Math.max(...timestamps))) : undefined;
};

const isCompliant = (latest: Date | undefined, today: Date): boolean => {
  if (!latest) {
    return false;
  }

  const due = addMonths(latest, COMPLIANCE_MONTHS);

  if (isBefore(due, today)) {
    return false;
  }

  return isBefore(addDays(today, WARNING_DAYS), due);
};

const RenfrewOverview: React.FC<Props> = ({ program }) => {
  const user = useRecoilValueLoadable<IUser | null>(userAtom);
  const evidence = useRecoilValueLoadable<IEvidence[] | null>(evidenceAtom);

  const { programEvidence, draftCount } = useMemo(() => {
    const contents = evidence.state === "hasValue" ? evidence.contents ?? [] : [];
    const forProgram = contents.filter((item) => {
      const programName = item.evidenceJSON?.programInfo?.program || "";
      const onHoldReset = item.evidenceJSON?.OnHoldReset ?? 0;

      return programName === program.Name && onHoldReset !== 1;
    });

    return {
      programEvidence: forProgram.filter((item) => !item.draft),
      draftCount: forProgram.filter((item) => item.draft).length,
    };
  }, [evidence.state, evidence.contents, program.Name]);

  const sections = useMemo(() => {
    const level =
      user.state === "hasValue"
        ? ProgramUtils.getSkillLevelForProgram(user.contents, program)
        : program.Levels?.[0] || "";
    const skills = ProgramUtils.filterSkillsByLevel(program.Skills ?? [], level).filter((skill) => skill.Section);
    const today = startOfDay(new Date());

    return _.map(
      _.groupBy(skills, (skill) => skill.Section),
      (sectionSkills, section) => ({
        section,
        compliantCount: sectionSkills.filter((skill) =>
          isCompliant(latestEvidenceDate(skill, programEvidence, program), today)
        ).length,
        total: sectionSkills.length,
      })
    );
  }, [user.state, user.contents, program, programEvidence]);

  return (
    <div>
      {sections.map((section) => (
        <div
          className="mt-4"
          key={section.section}
        >
          <SegmentedProgressBar
            title={section.section}
            numerator={section.compliantCount}
            denominator={section.total}
          />
        </div>
      ))}
      <div className="mt-4 flex border-t border-grey-30 pt-4">
        <div className="flex flex-1 flex-col justify-between">
          <div className="mb-2 text-acc-13px font-normal text-grey-90">{"Evidence added"}</div>
          <div className="text-acc-28px font-bold text-program-card-eprr-text">{programEvidence.length}</div>
        </div>
        <div className="flex flex-1 flex-col items-start justify-between">
          <div className="mb-2 rounded-xl bg-draft-badge px-2 py-1 text-acc-12px font-semibold text-draft-badge-text">
            {"Draft"}
          </div>
          <div className="text-acc-28px font-bold text-program-card-eprr-text">{draftCount}</div>
        </div>
      </div>
    </div>
  );
};

export default RenfrewOverview;
